import {Header} from '@tanstack/react-table'
import {
    StyledTableWrapper,
    StyledTableBodyCell,
    StyledTableBodyRow,
    StyledTableColumnResizer,
    StyledTableHeader,
    StyledTableHeaderCell,
    StyledTableHeaderCellLabel,
    StyledTableContainerHorizontalScrollbar,
    StyledTableContainerVerticalScrollbar,
    StyledTablePagination,
    StyledTableContainer,
    StyledTableContainerScrollbarCorner,
    StyledBodyCellText,
    StyledSelectListWrapper,
    StyledSelectTrigger,
    StyledPreviousButton,
    StyledNextButton
} from '@components/ui/table-atoms/style.ts'
import {Button} from '@components/ui/button/Button.tsx'
import {ArrowRightIcon, ChevronDownIcon, ChevronSelectorVerticalIcon, ChevronUpIcon} from '@components/ui/icon/Icon.tsx'
import {TableProps} from '@components/commons/table/Table.tsx'
import {FC, forwardRef, HTMLAttributes, ReactNode, useMemo, useState, useEffect} from 'react'
import * as RadixScrollArea from '@radix-ui/react-scroll-area'
import {Flexbox} from '@components/ui/flexbox/FlexBox.tsx'
import {useTranslation} from 'react-i18next'
import {useTheme} from 'styled-components'
import {SelectPopover, SelectItem} from '@components/ui/select-atoms/SelectAtoms.tsx'
import {SelectProvider} from '@ariakit/react'
import useWindowDimensions from '@/hooks/useWindowDimensions'
import {Skeleton} from '@components/ui/skeleton/Skeleton.tsx'
import {Spinner} from '../spinner/Spinner'

/**
 * This component is the main parent of the table
 * */
export const TableWrapper = (props: HTMLAttributes<HTMLDivElement>) => (
    <StyledTableWrapper {...props}>{props.children}</StyledTableWrapper>
)

/**
 * This component is the "window" where the subcomponents live and handles scrolls
 * */
export const TableContainer: FC<HTMLAttributes<HTMLDivElement> & {hasPagination: boolean}> = ({
    children,
    hasPagination,
    ...rest
}) => (
    <StyledTableContainer {...rest}>
        <RadixScrollArea.ScrollArea style={{height: '100%'}}>
            <RadixScrollArea.Viewport style={{height: '100%'}}>
                {children}
                <StyledTableContainerVerticalScrollbar orientation="vertical" />
                <StyledTableContainerHorizontalScrollbar $hasPagination={hasPagination} orientation="horizontal" />
                <StyledTableContainerScrollbarCorner />
            </RadixScrollArea.Viewport>
        </RadixScrollArea.ScrollArea>
    </StyledTableContainer>
)

/**
 * This component is a simple header row that wraps the header cells
 * */
export const TableHeader = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>((props, ref) => (
    <StyledTableHeader {...props} ref={ref} />
))

interface TableHeaderCellProps extends HTMLAttributes<HTMLDivElement> {
    cellSize: number
    isPinned: boolean
}
/**
 * This component is a complex header cell that contains the passed children, also it manages the cell size and the sorting
 * */
export const TableHeaderCell: FC<TableHeaderCellProps> = ({cellSize, isPinned, ...rest}) => (
    <StyledTableHeaderCell $isPinned={isPinned} {...rest} style={{width: cellSize, ...rest.style}} />
)

/**
 * This component is a simple styled label for the header cell
 * */
export const TableHeaderCellLabel = (props: HTMLAttributes<HTMLDivElement>) => <StyledTableHeaderCellLabel {...props} />

interface TableBodyProps<TData extends unknown[]> extends HTMLAttributes<HTMLDivElement> {
    data: TData
    rowRender: (item: TData[number], index: number) => ReactNode
}
/**
 * This component is a container that renders the rows as children
 * */
export const TableBody = <TData extends unknown[]>({data, rowRender, ...rest}: TableBodyProps<TData>) => (
    <div {...rest}>{data.map((value, index) => rowRender(value, index))}</div>
)

export interface TableBodyRowProps extends HTMLAttributes<HTMLDivElement> {
    isSelected: boolean
}
/**
 * This component is a simple styled body row that wraps the cells
 * */
export const TableBodyRow: FC<TableBodyRowProps> = ({isSelected, ...rest}) => (
    <StyledTableBodyRow $isSelected={isSelected} {...rest} />
)

export interface TableBodyCellProps extends HTMLAttributes<HTMLDivElement> {
    cellSize: number
    isSelected?: boolean
    isPinned?: boolean
}
/**
 * This component is a simple styled cell that reacts to the cell size
 * */
export const TableBodyCell: FC<TableBodyCellProps> = ({cellSize, isPinned, isSelected, ...rest}) => (
    <StyledTableBodyCell
        $isSelected={isSelected}
        $isPinned={isPinned}
        {...rest}
        style={{width: cellSize, ...rest.style}}
    />
)

/**
 * This component is a simple styled cell text
 * */
interface TableBodyCellTextProps extends HTMLAttributes<HTMLDivElement> {
    variant?: 'light' | 'normal' | 'dark'
}
export const TableBodyCellText = forwardRef<HTMLDivElement, TableBodyCellTextProps>(({variant, ...rest}, ref) => (
    <StyledBodyCellText $variant={variant} ref={ref} {...rest} />
))

/**
 * This component is a simple styled element that set the cell size
 * */
export const TableColumnResizer = ({header}: {header: Header<unknown, unknown>}) => (
    <StyledTableColumnResizer
        className="column-resizer"
        onDoubleClick={() => header.column.resetSize()}
        onMouseDown={header.getResizeHandler()}
        onTouchStart={header.getResizeHandler()}
    />
)

/**
 * This component is a button that set the sorter
 * */
export const TableSortButton = <TData extends unknown[], TSortName extends string>({
    sortName,
    sorter
}: {
    sortName: TSortName
    sorter: NonNullable<TableProps<TData, TSortName>['sorter']>
}) => {
    if (sortName != sorter.sortName) {
        return (
            <Button size="xs" shape="square" variant="ghost" onClick={() => sorter.onSort(sortName, 'desc')}>
                <ChevronSelectorVerticalIcon />
            </Button>
        )
    }
    if (sorter.direction == 'desc') {
        return (
            <Button size="xs" shape="square" variant="ghost" onClick={() => sorter.onSort(sortName, 'asc')}>
                <ChevronDownIcon />
            </Button>
        )
    }

    return (
        <Button size="xs" shape="square" variant="ghost" onClick={() => sorter.onSort(null, null)}>
            <ChevronUpIcon />
        </Button>
    )
}

export interface TablePaginationProps {
    isSticky?: boolean
    pagination: {
        limit: number
        currentPage: number
        total: number
        onChangeLimit?: (limit: number) => void
        onChangePage: (page: number) => void
    }
    isLoading?: boolean
}

const getPages = (totalPages: number, currentPage: number): Array<number | string> => {
    if (totalPages <= 0) return []
    if (totalPages == 1) return [1]
    if (totalPages <= 2) {
        return Array.from({length: totalPages}, (_, index) => index + 1)
    }

    if (currentPage === 1) return [1, '...', totalPages]
    if (currentPage === totalPages) return [1, '...', totalPages]

    return [1, '...', currentPage, '...', totalPages]
}

/**
 * This component is the pagination bar
 * */
export const TablePagination: FC<TablePaginationProps> = ({pagination, isSticky = true, isLoading = false}) => {
    const {t} = useTranslation()
    const theme = useTheme()
    const lastPage = pagination.total >= pagination.limit ? Math.ceil(pagination.total / pagination.limit) : 1
    const pageNumbers = getPages(lastPage, pagination.currentPage)
    const limitOptions = [25, 50, 100]
    const [isOpen, setIsOpen] = useState(false)
    const [selectedLimit, setSelectedLimit] = useState(pagination.limit.toString())

    useEffect(() => {
        setSelectedLimit(pagination.limit.toString())
    }, [pagination.limit])

    const {width} = useWindowDimensions()
    const isMobile = useMemo(() => width < 768, [width])

    return (
        <StyledTablePagination $isSticky={isSticky}>
            {pagination.onChangeLimit &&
                (!isLoading ? (
                    <Flexbox gap={3} align="center" justify="center">
                        <SelectProvider open={isOpen} value={selectedLimit} setValue={setSelectedLimit}>
                            <StyledSelectTrigger
                                displayValue={() => selectedLimit}
                                onClick={() => setIsOpen(prev => !prev)}
                                showArrow
                            />
                            <SelectPopover
                                gutter={8}
                                sameWidth
                                unmountOnHide
                                onClose={() => {
                                    setIsOpen(false)
                                }}
                            >
                                <StyledSelectListWrapper>
                                    {limitOptions.map((option, index) => (
                                        <SelectItem
                                            disabled={isLoading}
                                            value={option.toString()}
                                            key={index}
                                            onClick={() => {
                                                setSelectedLimit(option.toString())
                                                pagination.onChangeLimit?.(option)
                                                setIsOpen(false)
                                            }}
                                        >
                                            {option}
                                        </SelectItem>
                                    ))}
                                </StyledSelectListWrapper>
                            </SelectPopover>
                        </SelectProvider>
                        <p>{t('measures:table:per_page')}</p>
                    </Flexbox>
                ) : (
                    <Skeleton width={100} height={24} />
                ))}

            <Flexbox gap={isMobile ? 4 : 8} align="center" justify={isMobile ? 'space-between' : 'end'} fullWidth>
                <StyledPreviousButton
                    disabled={pagination.currentPage == 1 || isLoading}
                    variant="neutral"
                    onClick={() => pagination.onChangePage(pagination.currentPage - 1)}
                >
                    <ArrowRightIcon />
                    <span>{t('commons:previous')}</span>
                </StyledPreviousButton>
                <Flexbox gap={0.5} align="center" justify="center">
                    {!isLoading ? (
                        pageNumbers.map((pageNumber, index) => (
                            <Button
                                style={{
                                    whiteSpace: 'nowrap',
                                    pointerEvents: pageNumber ? undefined : 'none',
                                    color:
                                        pageNumber !== pagination.currentPage ? theme.palette.neutral['500'] : undefined
                                }}
                                disabled={pageNumber === '...'}
                                shape={!pageNumber || +pageNumber < 9 ? 'square' : 'rectangle'}
                                size="xs"
                                variant={pageNumber == pagination.currentPage ? 'primaryLight' : 'ghost'}
                                onClick={pageNumber ? () => pagination.onChangePage(+pageNumber) : undefined}
                                key={index}
                            >
                                {pageNumber ?? pagination.currentPage}
                            </Button>
                        ))
                    ) : (
                        <Spinner />
                    )}
                </Flexbox>
                <StyledNextButton
                    disabled={pagination.currentPage == lastPage || isLoading}
                    variant="neutral"
                    onClick={() => pagination.onChangePage(pagination.currentPage + 1)}
                >
                    <span>{t('commons:next')}</span>
                    <ArrowRightIcon />
                </StyledNextButton>
            </Flexbox>
        </StyledTablePagination>
    )
}
